/**
 * Utilidades para manejo de datos demográficos jerárquicos
 */

/**
 * Obtiene la profundidad máxima de un árbol de nodos
 */
export const getMaxDepth = (nodes) => {
  if (!nodes || nodes.length === 0) return 0;
  let maxDepth = 0;
  const traverse = (list, depth) => {
    list.forEach((node) => {
      maxDepth = Math.max(maxDepth, depth);
      if (node.children && node.children.length > 0) {
        traverse(node.children, depth + 1);
      }
    });
  };
  traverse(nodes, 0);
  return maxDepth + 1;
};

/**
 * Busca un nodo por ID en un árbol jerárquico
 */
export const findNode = (nodes, id) => {
  if (!nodes || !Array.isArray(nodes)) return null;
  
  for (const node of nodes) {
    if (node.id === id) return node;
    if (node.children && node.children.length > 0) {
      const found = findNode(node.children, id);
      if (found) return found;
    }
  }
  return null;
};

/**
 * Busca el nombre (value) de un nodo por su ID
 */
export const findNodeName = (nodes, nodeId) => {
  if (!nodes || !Array.isArray(nodes)) return null;
  
  for (const node of nodes) {
    if (node.id === nodeId) {
      return node.value;
    }
    if (node.children && node.children.length > 0) {
      const found = findNodeName(node.children, nodeId);
      if (found) return found;
    }
  }
  return null;
};

/**
 * Encuentra la profundidad (depth) de un nodo en el árbol
 */
export const findNodeDepth = (nodes, nodeId, currentDepth = 0) => {
  if (!nodes || !Array.isArray(nodes)) return -1;

  for (const node of nodes) {
    if (node.id === nodeId) {
      return currentDepth;
    }
    if (node.children && node.children.length > 0) {
      const found = findNodeDepth(node.children, nodeId, currentDepth + 1);
      if (found !== -1) return found;
    }
  }
  return -1;
};

/**
 * Obtiene la ruta completa de nodos desde la raíz hasta el nodo seleccionado
 */
export const findNodePath = (nodes, nodeId, currentPath = []) => {
  if (!nodes || !Array.isArray(nodes)) return null;

  for (const node of nodes) {
    const nextPath = [...currentPath, node];

    if (node.id === nodeId) {
      return nextPath;
    }

    if (node.children && node.children.length > 0) {
      const found = findNodePath(node.children, nodeId, nextPath);
      if (found) return found;
    }
  }

  return null;
};

/**
 * Obtiene todos los descendientes de un nodo
 */
export const collectDescendantIds = (node) => {
  const ids = [];

  const traverse = (current) => {
    (current?.children || []).forEach((child) => {
      ids.push(child.id);
      traverse(child);
    });
  };

  traverse(node);
  return ids;
};

/**
 * Verifica si un nivel tiene opciones disponibles
 */
export const hasOptionsAtLevel = (demographic, level, selections) => {
  if (level === 0) {
    return demographic.nodes && demographic.nodes.length > 0;
  }

  const prevKey = `${demographic.demographicName}_level_${level - 1}`;
  const prevSelection = selections[prevKey];

  if (!prevSelection) return false;

  if (Array.isArray(prevSelection)) {
    return prevSelection.some((nodeId) => {
      const parentNode = findNode(demographic.nodes, nodeId);
      return parentNode?.children && parentNode.children.length > 0;
    });
  }

  const parentNode = findNode(demographic.nodes, prevSelection);
  return parentNode?.children && parentNode.children.length > 0;
};

/**
 * Verifica si un nivel es válido (tiene padre con hijos)
 */
export const isLevelValid = (demographicName, level, selections, demographicData) => {
  // El nivel 0 siempre es válido
  if (level === 0) return true;

  // Verificar que el nivel anterior existe y está seleccionado
  const prevKey = `${demographicName}_level_${level - 1}`;
  const prevSelection = selections[prevKey];
  
  if (!prevSelection) return false;

  // Buscar el demográfico
  const demographic = demographicData.find(
    (d) => d.demographicName === demographicName
  );
  
  if (!demographic) return false;

  if (Array.isArray(prevSelection)) {
    return prevSelection.some((nodeId) => {
      const parentNode = findNode(demographic.nodes, nodeId);
      return parentNode?.children && parentNode.children.length > 0;
    });
  }

  // Buscar el nodo padre
  const parentNode = findNode(demographic.nodes, prevSelection);
  
  // El nivel es válido si el padre tiene hijos
  return parentNode?.children && parentNode.children.length > 0;
};

/**
 * Obtiene el nombre del nivel según la profundidad
 */
export const getLevelNameByDepth = (demographic, depth) => {
  // Si el demographic tiene una configuración de niveles
  if (demographic?.levels && Array.isArray(demographic.levels)) {
    return (
      demographic.levels[depth]?.name ||
      demographic.levels[depth] ||
      `Nivel ${depth}`
    );
  }

  // Si hay un array de nombres de niveles
  if (demographic?.levelNames && Array.isArray(demographic.levelNames)) {
    return demographic.levelNames[depth] || `Nivel ${depth}`;
  }

  // Fallback: retornar el demographicName si es el primer nivel
  if (depth === 0) {
    return demographic?.demographicName || `Nivel ${depth}`;
  }

  return `Nivel ${depth}`;
};

/**
 * Valida que todos los niveles visibles de los demográficos estén completos
 */
export const validateAllDemographics = (demographics, selections) => {
  for (const demo of demographics) {
    const maxLevels = getMaxDepth(demo.nodes);

    if (demo.allowMultipleSelection === true) {
      const hasAnySelection = Array.from({ length: maxLevels }).some((_, level) => {
        const selectedValue = selections[`${demo.demographicName}_level_${level}`];
        return Array.isArray(selectedValue)
          ? selectedValue.length > 0
          : Boolean(selectedValue);
      });

      if (!hasAnySelection) return false;
      continue;
    }

    for (let level = 0; level < maxLevels; level++) {
      const key = `${demo.demographicName}_level_${level}`;
      const hasOptions = hasOptionsAtLevel(demo, level, selections);
      const selectedValue = selections[key];
      const hasSelection = Array.isArray(selectedValue)
        ? selectedValue.length > 0
        : Boolean(selectedValue);

      // Si tiene opciones y no está respondido, es inválido
      if (hasOptions && !hasSelection) {
        return false;
      }

      // Si no tiene opciones, detener validación de este demográfico
      if (!hasOptions) {
        break;
      }
    }
  }

  return true;
};

/**
 * Construye el array de respuestas demográficas filtradas y formateadas
 */
export const buildDemographicAnswers = (selections, demographicData) => {
  const multipleAnswers = demographicData
    .filter((demographic) => demographic.allowMultipleSelection === true)
    .flatMap((demographic) => {
      const maxLevels = getMaxDepth(demographic.nodes);
      const selectedIds = [];

      for (let level = 0; level < maxLevels; level++) {
        const key = `${demographic.demographicName}_level_${level}`;
        const selectedValue = selections[key];
        const values = Array.isArray(selectedValue)
          ? selectedValue
          : selectedValue
            ? [selectedValue]
            : [];

        selectedIds.push(...values);
      }

      const uniqueSelectedIds = [...new Set(selectedIds)];
      const terminalSelectedIds = uniqueSelectedIds.filter((nodeId) => {
        const node = findNode(demographic.nodes, nodeId);
        const descendants = collectDescendantIds(node);
        return !descendants.some((descendantId) =>
          uniqueSelectedIds.includes(descendantId)
        );
      });

      return terminalSelectedIds.map((nodeId) => {
        const path = findNodePath(demographic.nodes, nodeId) || [];
        const levelNames = path.map((_, index) =>
          getLevelNameByDepth(demographic, index)
        );
        const values = path.map((node) => node.value || node.id);
        const name = levelNames.join('>');
        const value = values.join('>');

        return {
          value,
          values: {},
          name,
          nameQuestion: name,
        };
      });
    });

  const singleAnswers = Object.entries(selections)
    .flatMap(([key, nodeValue]) => {
      const [demographicName, levelPart] = key.split('_level_');
      const level = parseInt(levelPart, 10);

      return (Array.isArray(nodeValue) ? nodeValue : [nodeValue]).map((nodeId) => ({
        key,
        nodeId,
        demographicName,
        level,
      }));
    })
    .filter(({ demographicName, level }) => {
      // Solo incluir si el nivel es válido
      return isLevelValid(demographicName, level, selections, demographicData);
    })
    .map(({ demographicName, level, nodeId }) => {
      // Buscar el demográfico completo
      const demographic = demographicData.find(
        (d) => d.demographicName === demographicName
      );

      if (demographic?.allowMultipleSelection === true) {
        return null;
      }

      // Buscar el nombre del nodo seleccionado
      const selectedNodeName = demographic
        ? findNodeName(demographic.nodes, nodeId)
        : nodeId;

      // Encontrar la profundidad real del nodo en el árbol
      const nodeDepth = demographic
        ? findNodeDepth(demographic.nodes, nodeId)
        : level;

      // Obtener el nombre del nivel según la profundidad
      const levelName = getLevelNameByDepth(demographic, nodeDepth);

      return {
        value: selectedNodeName || nodeId,
        values: {},
        name: levelName,
        nameQuestion: levelName,
      };
    })
    .filter(Boolean);

  return [...singleAnswers, ...multipleAnswers];
};

/**
 * Inversa de buildDemographicAnswers: de las respuestas guardadas vuelve a las
 * selecciones que entiende el formulario.
 *
 * Hace falta al retomar una encuesta pausada. El formulario se pinta desde
 * demographicSelections, que vive en sessionStorage y muere al cerrar la
 * pestaña, así que quien volvía al otro día -que es justo para lo que sirve
 * guardar y continuar- encontraba los demográficos vacíos y tenía que llenarlos
 * de nuevo, aunque el servidor los tuviera guardados.
 *
 * Lo guardado es {name, value}, donde name es el nombre del NIVEL y value el
 * texto del nodo. La clave que necesita el formulario es
 * `${nombreDelDemografico}_level_${profundidad}` apuntando al id del nodo, así
 * que hay que buscar cada valor dentro del árbol de opciones.
 *
 * Es deliberadamente tolerante: lo que no se pueda resolver -una opción que ya
 * no existe, un árbol que cambió- se omite y esa parte queda por responder, en
 * vez de romper el formulario entero.
 *
 * @param {Array} saved respuestas guardadas: [{name, value}]
 * @param {Array} demographicData árbol normalizado: [{demographicName, levelNames, nodes, allowMultipleSelection}]
 * @returns {Object} selecciones listas para el formulario
 */
export const buildSelectionsFromAnswers = (saved, demographicData) => {
  const selections = {};
  if (!Array.isArray(saved) || !Array.isArray(demographicData)) return selections;

  // Busca un nodo por su texto, opcionalmente limitando a una profundidad y a
  // los descendientes de un nodo ya elegido en el nivel anterior.
  const buscarNodo = (nodes, texto, profObjetivo, prof = 0) => {
    if (!Array.isArray(nodes)) return null;
    for (const node of nodes) {
      const coincide =
        (node.value ?? node.id) === texto &&
        (profObjetivo === undefined || prof === profObjetivo);
      if (coincide) return node;
      const hijo = buscarNodo(node.children, texto, profObjetivo, prof + 1);
      if (hijo) return hijo;
    }
    return null;
  };

  demographicData.forEach((demographic) => {
    const { demographicName, levelNames, nodes } = demographic;
    if (!demographicName || !Array.isArray(nodes)) return;

    const niveles =
      Array.isArray(levelNames) && levelNames.length > 0
        ? levelNames
        : [demographicName];

    // Rama donde seguir buscando: al bajar de nivel solo valen los hijos de lo
    // ya elegido, para no confundir dos opciones con el mismo nombre en ramas
    // distintas.
    let rama = nodes;

    niveles.forEach((levelName, profundidad) => {
      const guardado = saved.find(
        (s) => (s?.name ?? s?.id) === levelName && s?.value,
      );
      if (!guardado) return;

      // Los jerárquicos y los de selección múltiple se guardan como "a>b".
      const textos = String(guardado.value)
        .split('>')
        .map((t) => t.trim())
        .filter(Boolean);
      const texto = textos[textos.length - 1];

      const node =
        buscarNodo(rama, texto, 0) ?? buscarNodo(nodes, texto, profundidad);
      if (!node) return;

      selections[`${demographicName}_level_${profundidad}`] = node.id;
      rama = node.children || [];
    });
  });

  return selections;
};
